import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { merchantApi } from '../api/config';
import { swalConfirm } from './shared/swalConfirm';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faPlus, faClipboardList, faWallet, faKey, faLock, faShield,
  faBell, faBook, faCreditCard, faRightFromBracket, faUsers,
  faMoneyBillTransfer, faBars, faTimes, faUserCog, faChartBar, faMoneyBill, faVault, faRightLeft,
  faLifeRing, faCoins, faHandHoldingDollar, faChartPie,
  faSun, faMoon, faChevronLeft, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useWallet } from '../contexts/WalletContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useDarkMode } from '../hooks/useDarkMode';
import { usePermissions } from '../hooks/usePermissions';

const Layout: React.FC = () => {
  const { merchant, logout } = useAuth();
  const { wallet } = useWallet();
  const location = useLocation();
  const navigate = useNavigate();
  const { systemSettings } = useTheme();
  const { t } = useTranslation();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const twofaReminderRef = React.useRef<NodeJS.Timeout | null>(null);
  const twofaReminderOpenRef = React.useRef(false);

  useEffect(() => {
    if (location.pathname === '/2fa') return;
    if (twofaReminderOpenRef.current) return;

    const checkTwofaStatus = async () => {
      try {
        const response = await merchantApi.get('/2fa/status');
        const enabled = !!response.data?.data?.enabled;
        if (enabled) return;

        twofaReminderOpenRef.current = true;
        try {
          const goSetup = await swalConfirm({
            title: t('twofa.reminderTitle'),
            text: t('twofa.reminderMessage'),
            icon: 'warning',
            confirmText: t('twofa.reminderButton'),
            cancelText: t('twofa.reminderLater'),
          });
          if (goSetup) navigate('/2fa');
        } finally {
          twofaReminderOpenRef.current = false;
        }
      } catch (error) {
        // เช็คสถานะไม่สำเร็จ - ไม่ต้อง block UI
      }
    };

    checkTwofaStatus();
  }, [location.pathname, navigate, t]);

  useEffect(() => {
    if (isDesktop) {
      setMobileMenuOpen(false);
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isDesktop, mobileMenuOpen]);

  const isCompactSidebar = isDesktop && sidebarCollapsed;

  const closeMobileMenu = () => {
    if (!isDesktop) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    closeMobileMenu();
    logout();
    navigate('/login');
  };

  // Check if user is employee
  const { isEmployee, can } = usePermissions();

  const settlementPath = (
    isEmployee &&
    !can('can_create_settlement') &&
    can('can_view_settlement')
  ) ? '/settlement-list' : '/settlement';

  const allMenuGroups = [
    {
      group: null,
      items: [
        { path: '/', icon: faChartLine, label: 'Dashboard', requiredPermission: 'can_view_dashboard' },
      ],
    },
    {
      group: t('nav.groupOrders'),
      items: [
        { path: '/create-order', icon: faPlus, label: t('nav.createOrder'), requiredPermission: 'can_create_order' },
        { path: '/orders', icon: faClipboardList, label: t('nav.orders'), requiredPermission: 'can_view_orders' },
      ],
    },
    {
      group: t('nav.groupFinance'),
      items: [
        { path: '/wallet', icon: faWallet, label: 'Wallet', requiredPermission: 'can_manage_settlement' },
        { path: '/safe-wallet', icon: faVault, label: 'Safe Wallet', requiredPermission: 'can_manage_settlement' },
        { path: '/company-transfer', icon: faRightLeft, label: t('nav.companyTransfer'), ownerOnly: true },
        { path: settlementPath, icon: faMoneyBill, label: 'Settlement', requiredPermission: ['can_create_settlement', 'can_view_settlement'] },
        { path: '/payouts', icon: faMoneyBillTransfer, label: 'Payouts', requiredPermission: 'can_view_payouts' },
        { path: '/create-payout', icon: faPlus, label: t('nav.createPayout'), requiredPermission: 'can_create_payout' },
        { path: '/wallet-transactions', icon: faCoins, label: t('nav.walletHistory'), requiredPermission: ['can_view_transactions', 'can_manage_settlement'] },
      ],
    },
    {
      group: 'P2P',
      items: [
        // ⭐ เมนู + หน้า P2P เปิดให้ merchant ทุกร้าน (เจ้าของ + พนักงาน) ไม่ผูกกับ p2p_deposit_enabled
        //    — flag นั้นคุมเฉพาะ transparent routing ของ External API (/slip-orders, /payout) ไม่เกี่ยวกับ portal
        //    ฝั่งสร้าง P2P payout ยังคุมด้วย permission can_create_payout ที่ backend อยู่แล้ว
        { path: '/p2p-overview', icon: faChartPie, label: 'P2P Overview', requiredPermission: null },
        { path: '/deposit', icon: faHandHoldingDollar, label: 'ฝากเงิน (P2P)', requiredPermission: null },
        { path: '/p2p-payouts', icon: faMoneyBillTransfer, label: 'รับสภาพคล่อง (P2P)', requiredPermission: null },
      ],
    },
    {
      group: t('nav.groupReports'),
      items: [
        { path: '/analytics', icon: faChartBar, label: 'Analytics', requiredPermission: 'can_view_analytics' },
        { path: '/monthly-reports', icon: faChartBar, label: 'Monthly Reports', requiredPermission: 'can_view_analytics' },
      ],
    },
    {
      group: 'Support',
      items: [
        { path: '/tickets', icon: faLifeRing, label: 'Tickets', requiredPermission: 'can_view_tickets' },
      ],
    },
    {
      group: t('nav.groupSettings'),
      items: [
        { path: '/employees', icon: faUsers, label: 'Employees', requiredPermission: 'can_manage_employees' },
        { path: '/api-keys', icon: faKey, label: 'API Keys', requiredPermission: 'can_manage_settings' },
        { path: '/2fa', icon: faLock, label: '2FA' },
        { path: '/ip-whitelist', icon: faShield, label: 'IP Whitelist', requiredPermission: 'can_manage_settings' },
        { path: '/webhook', icon: faBell, label: 'Webhook', requiredPermission: 'can_manage_settings' },
        { path: '/telegram', icon: faTelegram, label: t('nav.telegram'), requiredPermission: 'can_manage_settings' },
        { path: '/api-docs', icon: faBook, label: 'API Docs', requiredPermission: 'can_manage_settings' },
        { path: '/profile', icon: faUserCog, label: 'Profile' },
      ],
    },
  ];

  // Filter menu items based on user type and permissions
  const filterItem = (item: any) => {
    // ซ่อนสำหรับพนักงาน (เห็นเฉพาะเจ้าของ merchant)
    if ((item.ownerOnly || item.merchantOnly) && isEmployee) return false;
    if (item.requiredPermission) return can(item.requiredPermission);
    return true;
  };

  const menuGroups = allMenuGroups
    .map(g => ({ ...g, items: (g.items as any[]).filter(filterItem) }))
    .filter(g => g.items.length > 0);

  return (
    <div className="flex min-h-screen bg-slate-100 dark:bg-slate-900">
      <div
        className={`fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={closeMobileMenu}
      />

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex w-72 flex-col bg-white shadow-2xl
          dark:bg-slate-800 dark:shadow-slate-900/60
          transition-transform duration-300 ease-in-out lg:transition-all lg:shadow-none
          lg:border-r lg:border-gray-200/70 dark:lg:border-slate-700/70
          ${isCompactSidebar ? 'lg:w-20' : 'lg:w-72'}
          ${mobileMenuOpen || isDesktop ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Floating collapse handle — Desktop only */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 z-50 hidden lg:flex
                     h-6 w-6 items-center justify-center
                     rounded-full bg-white border border-gray-200 shadow-md
                     dark:bg-slate-700 dark:border-slate-600
                     text-gray-400 hover:text-gray-700 hover:border-gray-300 hover:shadow-lg hover:scale-110
                     dark:text-slate-400 dark:hover:text-slate-200 dark:hover:border-slate-500
                     transition-all duration-200"
          title={sidebarCollapsed ? 'ขยาย sidebar' : 'ย่อ sidebar'}
        >
          <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} className="text-[8px]" />
        </button>
        {/* Top gradient accent strip */}
        <div className="h-[3px] bg-gradient-primary flex-shrink-0" />

        {/* Header / Logo Section */}
        <div className={`relative px-4 pt-5 pb-4 border-b border-gray-100 dark:border-slate-700 ${isCompactSidebar ? 'text-center' : ''}`}>
          {/* Close - Mobile */}
          <button
            type="button"
            onClick={closeMobileMenu}
            className="absolute top-[18px] right-3 flex h-7 w-7 items-center justify-center rounded-lg
                       text-gray-400 hover:text-gray-600 hover:bg-gray-100
                       dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700
                       transition-all duration-200 lg:hidden"
            aria-label="Close navigation menu"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          {!isCompactSidebar ? (
            <>
              {systemSettings.system_logo_url ? (
                <img
                  src={systemSettings.system_logo_url}
                  alt="Logo"
                  className="max-h-16 w-auto max-w-full object-contain mb-4"
                />
              ) : (
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white"
                    style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
                  >
                    <FontAwesomeIcon icon={faCreditCard} className="text-[15px]" />
                  </div>
                  <div>
                    <h1 className="text-gray-800 dark:text-slate-100 font-semibold text-[15px] tracking-tight leading-none">Merchant</h1>
                    <p className="text-[9px] font-medium uppercase tracking-[0.15em] mt-[3px] text-gray-400 dark:text-slate-500">
                      Portal
                    </p>
                  </div>
                </div>
              )}

              {/* User Info Card */}
              <div className="rounded-xl overflow-hidden bg-gray-50 border border-gray-100/80 dark:bg-slate-700/50 dark:border-slate-700/80">
                <div className="h-[2px] bg-gradient-primary" />
                <div className="p-2.5">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
                      style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
                    >
                      {(merchant?.name || 'M').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate leading-none mb-[5px] text-gray-700 dark:text-slate-200">
                        {merchant?.name || 'Merchant'}
                      </p>
                      <p className="text-[10px] flex items-center gap-1.5 leading-none text-gray-400 dark:text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: '#10b981' }} />
                        {isEmployee ? 'Employee' : (
                          <>P.In: {merchant?.fee_percentage ?? 0}% · P.Out: {merchant?.payout_fee_percentage ?? 0}%</>
                        )}
                      </p>
                    </div>
                  </div>
                  {/* Wallet Balance */}
                  <div className="grid grid-cols-2 gap-1.5 pt-2.5 border-t border-gray-100 dark:border-slate-700">
                    <div className="bg-white border border-gray-100 rounded-lg px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700">
                      <p className="text-[9px] uppercase tracking-wider mb-0.5 text-gray-400 dark:text-slate-500">Wallet</p>
                      <p className="text-xs font-bold truncate text-gray-800 dark:text-slate-100">
                        ฿{((wallet?.is_frozen ? wallet?.available_balance : wallet?.balance) ?? 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-lg px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700">
                      <p className="text-[9px] uppercase tracking-wider mb-0.5 text-gray-400 dark:text-slate-500">Payout</p>
                      <p className="text-xs font-bold truncate text-indigo-600 dark:text-indigo-300">
                        ฿{(wallet?.payout_balance ?? 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              className="w-9 h-9 mx-auto rounded-xl flex items-center justify-center text-white mt-5"
              style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
            >
              <FontAwesomeIcon icon={faCreditCard} className="text-[15px]" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 py-3 overflow-y-auto ${isCompactSidebar ? 'px-1' : 'px-2.5 custom-scrollbar-light'}`}
          style={isCompactSidebar ? { scrollbarWidth: 'none' } : undefined}
        >
          <div className="space-y-4">
            {menuGroups.map((group, gi) => (
              <div key={gi}>
                {/* Group Label */}
                {group.group && !isCompactSidebar && (
                  <div className="flex items-center gap-2 px-1 mb-2">
                    <div
                      className="w-3 h-[1.5px] rounded-full flex-shrink-0"
                      style={{ background: 'var(--theme-primary)', opacity: 0.5 }}
                    />
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.12em] leading-none text-gray-400 dark:text-slate-600">
                      {group.group}
                    </p>
                    <div className="flex-1 h-[1px] rounded-full bg-gray-100 dark:bg-slate-700" />
                  </div>
                )}
                {group.group && isCompactSidebar && (
                  <div className="mx-3 mb-2 h-[1px] bg-gray-100 dark:bg-slate-700" />
                )}

                <div className="space-y-[2px]">
                  {group.items.map((item: any) => {
                    const isActive = location.pathname === item.path;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        title={isCompactSidebar ? item.label : undefined}
                        onClick={closeMobileMenu}
                        className={`
                          flex items-center gap-3 py-2.5 rounded-xl text-sm relative group
                          transition-all duration-200
                          ${isCompactSidebar ? 'justify-center px-0' : 'pl-3.5 pr-3'}
                          ${isActive
                            ? 'font-medium text-white'
                            : 'font-normal text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700/50'
                          }
                        `}
                        style={isActive ? {
                          background: 'var(--theme-primary-gradient)',
                          boxShadow: 'var(--theme-shadow-primary)',
                        } : undefined}
                      >
                        {/* Icon */}
                        <span className={`text-[13px] flex-shrink-0 transition-colors duration-200 ${
                          isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500 dark:text-slate-500 dark:group-hover:text-slate-400'
                        }`}>
                          <FontAwesomeIcon icon={item.icon} />
                        </span>

                        {!isCompactSidebar && (
                          <span className="truncate">{item.label}</span>
                        )}

                        {/* Compact mode tooltip */}
                        {isCompactSidebar && (
                          <div
                            className="absolute left-full ml-3 px-3 py-1.5 text-gray-700 bg-white text-xs rounded-lg
                                       dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600
                                       opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                       transition-all duration-200 whitespace-nowrap z-50 shadow-lg border border-gray-200"
                          >
                            {item.label}
                            <div className={`absolute left-0 top-1/2 -translate-x-[7px] -translate-y-1/2 border-[5px] border-transparent ${isDark ? 'border-r-slate-700' : 'border-r-white'}`} />
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Logout Section */}
        <div className="px-2.5 pb-3 pt-2.5 border-t border-gray-100 dark:border-slate-700">
          <button
            onClick={handleLogout}
            title={isCompactSidebar ? t('common.logout') : undefined}
            className={`
              w-full py-2.5 px-3.5 rounded-xl text-sm font-medium
              flex items-center gap-3 transition-all duration-200 cursor-pointer
              border border-gray-200 bg-gray-50 text-gray-400
              hover:text-red-500 hover:bg-red-50 hover:border-red-200
              dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-500
              dark:hover:text-red-400 dark:hover:bg-red-900/20 dark:hover:border-red-800
              ${isCompactSidebar ? 'justify-center' : ''}
            `}
          >
            <FontAwesomeIcon icon={faRightFromBracket} />
            {!isCompactSidebar && <span>{t('common.logout')}</span>}
          </button>

          {/* Version */}
          {!isCompactSidebar && (
            <p className="text-center text-[9.5px] mt-2.5 tracking-wide text-gray-300 dark:text-slate-700">
              Merchant Portal v2.0
            </p>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        ml-0 flex-1 min-w-0 min-h-screen overflow-x-clip transition-all duration-300
        ${isCompactSidebar ? 'lg:ml-20' : 'lg:ml-72'}
      `}>
        {/* Top Header Bar — ใช้ overflow-x-clip บน main (ไม่ใช่ hidden) ไม่งั้น sticky ไม่ยึด viewport */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-gray-200/60 bg-white/80 px-4 py-3 backdrop-blur-md sm:px-6 dark:border-slate-700/60 dark:bg-slate-800/80">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200/70 text-gray-600 transition-colors hover:bg-gray-50 lg:hidden dark:border-slate-700/70 dark:text-slate-400 dark:hover:bg-slate-700/50"
            aria-label="Open navigation menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <div className="flex items-center gap-2 ml-auto">
            {/* Dark mode toggle */}
            <button
              type="button"
              onClick={toggleDark}
              title={isDark ? 'เปลี่ยนเป็น Light Mode' : 'เปลี่ยนเป็น Dark Mode'}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200/70 text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:border-slate-700/70 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            >
              <FontAwesomeIcon icon={isDark ? faSun : faMoon} className="text-sm" />
            </button>
            <LanguageSwitcher />
          </div>
        </div>

        <div className="p-4 sm:p-6 min-h-[calc(100vh-57px)]">
          <Outlet />
        </div>
      </main>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar-light::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
      `}</style>
    </div>
  );
};

export default Layout;
